import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Check, Shield, Zap, Calendar, Lock, Crown, Users, Target } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function Upgrade() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/subscription/checkout');
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to start upgrade process");
      setLoading(false);
    }
  };

  return (
    <div className={cn(
      "min-h-screen",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      
      <div className="max-w-4xl mx-auto p-6 md:p-12">
        <div className="text-center mb-12 space-y-4">
          <h1 className={cn(
            "text-4xl font-bold tracking-tight",
            isDark ? "text-gradient-shine" : "text-[#1A1A1A]"
          )}>Unlock Your Full Potential</h1>
          <p className={cn("text-xl", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Get advanced tracking tools to crush your goals.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <Card className={cn(
            "flex flex-col",
            isDark ? "glass-card border-zinc-800" : "bg-white border-[#EEEAE0] shadow-sm"
          )}>
            <CardHeader className="pb-4">
              <CardTitle className={cn("text-2xl", isDark ? "text-white" : "text-[#1A1A1A]")}>Free Arena</CardTitle>
              <CardDescription className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>Basic goal tracking</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 flex-1">
              <div className={cn("text-3xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>
                $0 <span className={cn("text-sm font-normal", isDark ? "text-zinc-500" : "text-[#9E8778]")}>/ forever</span>
              </div>
              <ul className={cn("space-y-3 pt-4", isDark ? "text-zinc-300" : "text-[#5F5F5F]")}>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Goal setting & tracking</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Daily progress logs</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Basic stats</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Up to 10 goals</span>
                </li>
                <li className={cn("flex items-center gap-3", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                  <Lock className="w-5 h-5 flex-shrink-0" />
                  <span>No performance tracking</span>
                </li>
                <li className={cn("flex items-center gap-3", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                  <Lock className="w-5 h-5 flex-shrink-0" />
                  <span>No goal groups</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="pt-4">
              <Button 
                variant="outline" 
                className={cn(
                  "w-full h-12",
                  isDark ? "border-zinc-700 text-zinc-400" : "border-[#E5E2DA] text-[#5F5F5F]"
                )}
                disabled
              >
                Current Plan
              </Button>
            </CardFooter>
          </Card>

          {/* Premium Plan */}
          <Card className={cn(
            "flex flex-col relative overflow-hidden border-coral/50",
            isDark ? "glass-card" : "bg-white shadow-lg"
          )}>
            {/* Recommended Badge */}
            <div className="absolute top-0 right-0">
              <div className="bg-coral text-white text-xs font-bold px-3 py-1.5 rounded-bl-lg">
                RECOMMENDED
              </div>
            </div>
            
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-coral" />
                <CardTitle className={cn("text-2xl", isDark ? "text-gradient" : "text-coral")}>Monk Mode Pro</CardTitle>
              </div>
              <CardDescription className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>Everything you need for serious accountability</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 flex-1">
              <div className={cn("text-3xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>
                $4.99 <span className={cn("text-sm font-normal", isDark ? "text-zinc-500" : "text-[#9E8778]")}>/ month</span>
              </div>

              <ul className={cn("space-y-3 pt-2", isDark ? "text-zinc-300" : "text-[#5F5F5F]")}>
                <li className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-coral flex-shrink-0" />
                  <span className="font-medium">Unlimited Goals</span>
                </li>
                <li className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-coral flex-shrink-0" />
                  <span className="font-medium">Shared Goals with Friends</span>
                </li>
                <li className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-coral flex-shrink-0" />
                  <span className="font-medium">Weekly Habit Tracker</span>
                </li>
                <li className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-coral flex-shrink-0" />
                  <span className="font-medium">Goal Groups & Organization</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-coral flex-shrink-0" />
                  <span className="font-medium">Performance Calendar</span>
                </li>
                <li className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-coral flex-shrink-0" />
                  <span className="font-medium">Priority Support</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="pt-4">
              <Button 
                onClick={handleUpgrade} 
                className="w-full h-12 text-lg font-bold btn-glossy"
                disabled={loading || user?.is_premium}
              >
                {user?.is_premium ? "You're Already Pro!" : loading ? "Processing..." : "Upgrade Now"}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
